<script>
    import { onMount } from "svelte";

    import { user } from "../../../stores";

    import { apiMethods } from "../../../Services/index.svelte";

    import {
        getAmountInCurrency,
        isObjectEmpty,
        showError,
        showException,
        showInfo,
    } from "../../../Utils/utilFunctions.svelte";

    import {
        insuranceTypeHideQuoteSections,
        insuranceTypesMayAllowAmaBillingRule,
        insuranceTypesNotAllowBillingRule,
    } from "../../../Utils/utilConstants.svelte";

    import {
        NotificationModal,
        PageLoading,
    } from "../../../Components/Common/index.svelte";

    import {
        billingRuleCalculateTotals,
        getMinHourlyRate,
        getMinTotalFee,
        patientPaysTotalFeeBasedOnCalculation,
        syncItemsAfterCalculation,
    } from "../helpers/billingRuleHelper.svelte";

    import {
        copyQuoteItemsToInvoice,
        updateEncounterLatestBillingData,
    } from "../helpers/encounterHelper.svelte";

    import PatientEncounterBillingDetails from "../components/PatientEncounterBillingDetails.svelte";
    import PatientEncounterItems from "../components/PatientEncounterItems.svelte";
    import PatientEncounterBillingLatestDetails from "../components/PatientEncounterBillingLatestDetails.svelte";
    import PatientEncounterQuotesTable from "../components/PatientEncounterQuotesTable.svelte";
    import PatientEncounterInvoicesTable from "../components/PatientEncounterInvoicesTable.svelte";
    import PatientEncounterPaymentsTable from "../components/PatientEncounterPaymentsTable.svelte";

    import { DirectInsuranceType } from "../../../Utils/utilEnums.svelte";

    export let patientEncounterDetails = {},
        billingDetails = {},
        latestQuoteData = {},
        latestQuoteDataItems = [],
        latestInvoiceData = {},
        latestInvoiceDataItems = [],
        quotesData = [],
        invoicesData = [],
        paymentsData = [],
        doctorProviders = [],
        reloadBillingDetails = () => {},
        reloadPatientEncounter = () => {},
        isUpdatingBillingStatus = false,
        itemsNotSaved = false,
        logsLoaded = false,
        isFromCalendarList = false;

    $: isUserDoctor = $user.role == 2;

    $: hasActiveQuote = patientEncounterDetails.estimates && patientEncounterDetails.estimates.length && patientEncounterDetails.estimates.some((e) => e.status != 9)

    $: isUserEnableSeePaymentReceive =
        $user.role == 100 || $user.role == 90 || $user.role == 20;

    $: itemsNotSaved = quoteItemsNotSaved || invoiceItemsNotSaved;

    $: isEncounterTypeLabourEpidural = patientEncounterDetails.typeOfBill == 2;

    $: isQuoteSectionsHideByInsuranceTypes =
        insuranceTypeHideQuoteSections.includes(
            patientEncounterDetails?.patient?.insuranceType,
        );

    // Private insurance Fund ID 13 is for "(The) Doctors' Health Fund Limited"
    $: isEncounterPrivateInsuranceSkipQuoteSection =
        patientEncounterDetails?.patient?.insuranceType == 0 &&
        patientEncounterDetails?.patient?.insuranceId == 13 &&
        patientEncounterDetails?.patient?.insurance == "Top Cover";

    $: isQuoteSectionsHidden =
        isEncounterTypeLabourEpidural ||
        isQuoteSectionsHideByInsuranceTypes ||
        isEncounterPrivateInsuranceSkipQuoteSection;

    // check if valid quote or invoice associated with the encounter when
    // 1. click select billing rule
    // 2. edit items
    // 3. change billing type figures (gap, rebate, total fee)
    $: quote_scenario_one_fulfilled =
        !isObjectEmpty(latestQuoteData) && quotesData.length;
    $: invoice_scenario_one_fulfilled =
        !isObjectEmpty(latestInvoiceData) && invoicesData.length;

    // if scenario_one_fulfilled, check if any payments with status Received(1) associated with it
    $: quote_scenario_two_fulfilled =
        quote_scenario_one_fulfilled &&
        paymentsData.length &&
        paymentsData.some(
            (payment) =>
                payment.patientEncounterBillingDataId == latestQuoteData.id &&
                payment.status == 1,
        );
    $: invoice_scenario_two_fulfilled =
        invoice_scenario_one_fulfilled &&
        paymentsData.length &&
        paymentsData.some(
            (payment) =>
                payment.patientEncounterBillingDataId == latestInvoiceData.id &&
                payment.status == 1,
        );

    $: quote_latest_billing_data_scenario_two_fulfilled =
        quote_scenario_one_fulfilled &&
        paymentsData.length &&
        paymentsData.some(
            (payment) =>
                payment.patientEncounterBillingDataId == latestQuoteData.id &&
                payment.status != 0 &&
                payment.status != 9,
        );
    $: invoice_latest_billing_data_scenario_two_fulfilled =
        invoice_scenario_two_fulfilled &&
        paymentsData.length &&
        paymentsData.some(
            (payment) =>
                payment.patientEncounterBillingDataId == latestQuoteData.id &&
                payment.status != 0 &&
                payment.status != 9,
        );

    $: totalPaymentsAmount = _getTotalPayments(quotesData, invoicesData);

    let isRegenerating = false;

    let quoteItemsRef, invoiceItemsRef, paymentsTableRef;

    let __BackupLatestQuoteItems__ = [],
        __BackupLatestInvoiceItems__ = [];

    let quoteItemsNotSaved = false,
        invoiceItemsNotSaved = false;

    let isEstimateItemsShowSelectBillingRule = false,
        isInvoiceItemsShowSelectBillingRule = false;

    export let doctorPresets = [],
        hasDoctorPresetsFetched = false;
    let isLoadingDoctorPresets = false;

    export let doctorBillingRules = [],
        hasDoctorBillingRulesFetched = false;
    let isLoadingDoctorBillingRules = false;

    let needShowWarningExceedOutsideFundRulesModal = false,
        showWarningExceedOutsideFundRulesModal = false;

    onMount(async () => {
        __BackupLatestQuoteItems__ = [...latestQuoteDataItems];
        __BackupLatestInvoiceItems__ = [...latestInvoiceDataItems];

        await loadDoctorPresets();

        await loadDoctorBillingRules();
    });

    const loadDoctorPresets = async () => {
        if (doctorPresets.length || hasDoctorPresetsFetched) return;

        isLoadingDoctorPresets = true;

        await apiMethods.getMethod
            .getAccountPresets(0, 999999, {
                "filter[doctorId]": patientEncounterDetails.doctorId,
            })
            .then((res) => {
                if (!res || res.error)
                    showError(
                        res.error ??
                            "Failed to load doctor presets, please refresh the page and try again and contact support if the issue persists.",
                    );
                else {
                    doctorPresets = [...res.results];
                    hasDoctorPresetsFetched = true;
                }
            })
            .catch((err) => showError(err));

        isLoadingDoctorPresets = false;
    };

    const loadDoctorBillingRules = async () => {
        if (doctorBillingRules.length || hasDoctorBillingRulesFetched) return;

        isLoadingDoctorBillingRules = true;

        await apiMethods.getMethod
            .getBillingRules(0, 999999, {
                "filter[doctorId]": patientEncounterDetails.doctorId,
            })
            .then((res) => {
                if (!res || res.error)
                    showError(
                        res.error ??
                            "Failed to load doctor billing rules, please refresh the page and try again and contact support if the issue persists.",
                    );
                else {
                    doctorBillingRules = [...res.results];
                    hasDoctorBillingRulesFetched = true;
                }
            })
            .catch((err) => showError(err));

        isLoadingDoctorBillingRules = false;
    };

    const regenerateQuoteOrInvoice = async (
        obj,
        isRecalculate,
        type,
        isItemChange = false,
        skipRegenerateAndCalculate = false,
        oldObj = {},
    ) => {
        isRegenerating = true;

        if (!type) {
            await reloadBillingDetails();
            isRegenerating = false;
            return;
        }

        if (!skipRegenerateAndCalculate) {
            if (!isRecalculate) await regenerateBilling(obj, type);
            else await calculateAndSyncTotals(obj, isItemChange, oldObj);

            await reloadBillingDetailsAndUpdateDefaultItems(
                false,
                patientEncounterDetails,
            );
        }

        if (type && type.toLowerCase() == "quote" && (!invoicesData.length || invoicesData.filter(i => i.status != 9).length == 0)) {
            patientEncounterDetails = await copyQuoteItemsToInvoice(
                patientEncounterDetails,
                reloadBillingDetails,
            );
            //calculate totals on invoice
            if(oldObj.billingCodesType != 2) {
                //if not free text items
                var invoiceObj = patientEncounterDetails.billingData.find((b) => b.isLatestInvoiceData);
                if(invoiceObj) {
                    await calculateAndSyncTotals(invoiceObj, true, {});
                }
            }

            await reloadBillingDetailsAndUpdateDefaultItems(
                false,
                patientEncounterDetails,
            );
        }

        isRegenerating = false;
    };

    const calculateAndSyncTotals = async (
        latestObj,
        isItemChange,
        oldLatestObj,
    ) => {
        const {
            insuranceType,
            billingType,
            usingAmaRates,
            useAsaCodes,
            actualGap,
            calculationAmount,
        } = latestObj;

        if (insuranceTypesNotAllowBillingRule.includes(insuranceType)) {
            // res here would have new property called "usingAmaRates" which needs to be updated in latest billing data object
            let res = await billingRuleCalculateTotals(
                patientEncounterDetails.doctorId,
                patientEncounterDetails.id,
                latestObj.id,
            );

            if (res.error) showError(res.error, undefined, true);
            else
                latestObj = await syncItemsAfterCalculation(
                    res.resolve,
                    latestObj,
                );
        } else {
            let fundData;

            await apiMethods.getMethod
                .getPatientEncounterFundData(latestObj.id)
                .then((res) => (fundData = res));

            // update mbs items fieldChange in order to calculate totals correctly
            if (!useAsaCodes) {
                const { mbsData } = latestObj;
                const { items } = fundData;

                latestObj.mbsData = mbsData.map((data) => {
                    const targetItem = items.find(
                        (item) =>
                            data.itemNum == item.itemIdentifier &&
                            data.subItemNum == item.subItemIdentifier,
                    );

                    if (targetItem)
                        return {
                            ...data,
                            fieldChange: targetItem.fieldQuantity ?? Number(0),
                        };

                    return data;
                });
            }

            // when re-calculate, calculate min amount value beforehand and set to minAmount if current calculationAmount smaller
            let minAmount = await _getItemsTotalMinAmount(latestObj, fundData);

            let requiredAmount = calculationAmount
                ? Math.max(minAmount, calculationAmount)
                : minAmount;

            // when item changes, if rule is "Set unit value" and actualGap is 0, adjust amount = calculated minAmount, AND NOT PIP INS TYPE
            if (
                isItemChange &&
                !useAsaCodes &&
                !usingAmaRates &&
                billingType == 2 &&
                actualGap <= 0 &&
                insuranceType != 30 &&
                getAmountInCurrency(requiredAmount) !=
                    getAmountInCurrency(minAmount)
            ) {
                requiredAmount = minAmount;
                showInfo(
                    `Unit value updated to ${getAmountInCurrency(requiredAmount)} to maintain 0 for the gap.`,
                );
            }

            let calculateObj = {};

            // if insurance WC or TAC or ADF and not using AMA rates, calculate with type 3 and amount 0
            if (
                insuranceTypesMayAllowAmaBillingRule.includes(insuranceType) &&
                !usingAmaRates
            )
                calculateObj = {
                    type: 3,
                    amount: 0,
                };
            // when billingType == 1 ("No Gap"), set type = 3 && amount = 0
            else
                calculateObj = {
                    type: billingType == 1 ? 3 : billingType,
                    amount: billingType == 1 ? 0 : requiredAmount,
                };

            // sync billingType to calculateObj type
            latestObj.billingType = calculateObj.type;

            // avoid run calculate totals api when no billing rule is selected
            if (
                (calculateObj.type && calculateObj.amount) ||
                (calculateObj.type && calculateObj.type == 3) ||
                insuranceTypesNotAllowBillingRule.includes(insuranceType) ||
                insuranceTypesMayAllowAmaBillingRule.includes(insuranceType)
            ) {
                // res here would have new property called "usingAmaRates" which needs to be updated in latest billing data object
                let res = await billingRuleCalculateTotals(
                    patientEncounterDetails.doctorId,
                    patientEncounterDetails.id,
                    latestObj.id,
                    calculateObj,
                );

                const patientPaysResponse = await patientPaysTotalFeeBasedOnCalculation(
                    false,
                    latestObj.insuranceType,
                    {
                        ...res.resolve,
                        withinFundRules: res.resolve.whitinFundRules,
                    },
                    latestObj
                );
                
                if(patientPaysResponse != null){
                    const { patientPaysTotalFee } = patientPaysResponse;
                    latestObj.patientPaysTotalFee = Boolean(patientPaysTotalFee);
                }


                if (res.error) showError(res.error, undefined, true);
                else {
                    latestObj = await syncItemsAfterCalculation(
                        res.resolve,
                        latestObj,
                        fundData,
                    );
                    latestObj.calculationAmount = requiredAmount;

                    needShowWarningExceedOutsideFundRulesModal =
                        latestObj.isLatestInvoiceData &&
                        latestObj.insuranceType ==
                            DirectInsuranceType.Private &&
                        latestObj.oopType == 1 &&
                        oldLatestObj.withinFundRules &&
                        oldLatestObj.withinFundRules !=
                            latestObj.withinFundRules &&
                        latestObj.patientPaysTotalFee &&
                        latestObj.patientPaysTotalFee !=
                            oldLatestObj.patientPaysTotalFee;
                }
            }
        }

        // when re-calculate with saved value, might cause data scaling
        if (latestObj.actualGap >= 0)
            await updateEncounterBillingDetails(latestObj);
        else if (latestObj.actualGap > -0.1) {
            latestObj.actualGap = 0;
            await updateEncounterBillingDetails(latestObj);
        } else
            showError(
                "Invalid gap amount, please refresh the page and try again and contact support if the issue persists.",
                undefined,
                true,
            );
    };

    const updateEncounterBillingDetails = async (obj) => {
        patientEncounterDetails.billingData =
            patientEncounterDetails.billingData.map((bd) => {
                if (bd.id == obj.id) return { ...obj };
                else return bd;
            });

        patientEncounterDetails = updateEncounterLatestBillingData(
            patientEncounterDetails,
        );

        await apiMethods.putMethod
            .updatePatientEncounter(patientEncounterDetails, true, "Update billing details")
            .then(async (res) => {
                if (!res)
                    showError(
                        "Failed to update encounter details, please refresh the page and try again and contact support if the issue persists.",
                        undefined,
                        true,
                    );
                else if (res.error) showError(res.error, undefined, true);
                // else patientEncounterDetails = { ...res };
                else reloadBillingDetailsAndUpdateDefaultItems(false, res);
            })
            .catch((err) => showException(err));

        if (needShowWarningExceedOutsideFundRulesModal)
            _openWarningExceedOutsideFundRules();
    };

    const regenerateBilling = async (
        obj,
        billingDetailType,
        showResend = false,
    ) => {
        const pdfObj = {
            entityId: obj.id,
            doctorId: patientEncounterDetails.doctorId,
            patientId: patientEncounterDetails.patientId,
            patientEncounterId: patientEncounterDetails.id,
            type: billingDetailType.toLowerCase() == "invoice" ? 1 : 0,
        };

        await regeneratePdfFile(pdfObj, billingDetailType);
    };

    const regeneratePdfFile = async (obj, billingDetailType) => {
        await apiMethods.postMethod
            .regeneratePdfFile(obj)
            .then((res) => {
                if (!res.patientEncounterId)
                    showError(
                        `Unable to re-generate this ${billingDetailType.toLowerCase()} PDF, please refresh the page and try again and contact support if the issue persists.`,
                        undefined,
                        true,
                    );
            })
            .catch((err) =>
                showException(
                    `Unable to re-generate this ${billingDetailType.toLowerCase()} PDF, please refresh the page and try again and contact support if the issue persists.`,
                    undefined,
                ),
            );
    };

    const reloadBillingDetailsAndUpdateDefaultItems = async (
        updateCommunication = false,
        response = {},
    ) => {
        __BackupLatestQuoteItems__ = [];
        __BackupLatestInvoiceItems__ = [];

        await reloadBillingDetails(updateCommunication, response);

        __BackupLatestQuoteItems__ = [...latestQuoteDataItems];
        __BackupLatestInvoiceItems__ = [...latestInvoiceDataItems];
    };

    const onQuoteSendToPatient = async ({ detail }) =>
        await reloadBillingDetailsAndUpdateDefaultItems(true, detail);

    const reloadBillingItemsWhenTimeItemAdded = async (type = "") => {
        if (!type) return;

        // it looks like this bit of code is not used anywhere which was used to be used in latest billing details box when updating time item
        if (type.toLowerCase() == "quote") await quoteItemsRef.updateItems();
        else if (type.toLowerCase() == "invoice")
            await invoiceItemsRef.updateItems();
    };

    // HELPER FUNCTIONS
    const _getItemsTotalMinAmount = async (latestObjDetails, fundData) => {
        switch (latestObjDetails.billingType) {
            case 2: // Unit Value
                return _getMinUnitValue(fundData.items);
            case 3: // Gap Fee
                return 0;
            case 4: // Hourly Rate
                return getMinHourlyRate(
                    fundData,
                    latestObjDetails.isLatestQuoteData,
                    latestObjDetails,
                );
            case 5: // Total Fee
                return getMinTotalFee(fundData, latestObjDetails.billingCodesType == 1, latestObjDetails.insuranceType);
            case 6: // MBS Fee Percentage
                return 100;
            default:
                return 0;
        }
    };

    const _getMinUnitValue = (arr = []) => {
        if (!arr || !arr.length) return 0;

        let hfSum = 0,
            unitsSum = 0;

        arr.map(({ hfRebate, units }) => {
            hfSum += hfRebate;
            unitsSum += units;
        });

        return parseFloat(hfSum / unitsSum);
    };

    const _getTotalPayments = (quotes = [], invoices = []) => {
        let res = Number(0);

        if (!quotes.length && !invoices.length) return res;

        if (quotes.length)
            quotes.map(({ payments }) => {
                if (payments.length)
                    payments.map(({ amount, status }) => {
                        if ([1, 2].includes(status)) res += amount; // Received + Received Externally quotes
                        if ([5].includes(status)) res -= amount; // Refunded quotes
                    });
            });

        if (invoices.length)
            invoices.map(({ payments }) => {
                if (payments.length)
                    payments.map(({ amount, status }) => {
                        if ([1, 2].includes(status)) res += amount; // Received + Received Externally invoices
                        if ([5].includes(status)) res -= amount; // Refunded invoices
                    });
            });

        return res;
    };

    const _openWarningExceedOutsideFundRules = () => {
        showWarningExceedOutsideFundRulesModal = true;
        needShowWarningExceedOutsideFundRulesModal = false;
    };

    const _closeWarningExceedOutsideFundRules = () =>
        (showWarningExceedOutsideFundRulesModal = false);
</script>

<div class="relative flex-1">
    <div class="flex flex-col overflow-y-auto absolute inset-0 gap-4 mt-4">
        <PatientEncounterBillingDetails
            bind:patientEncounterDetails
            {billingDetails}
            {latestQuoteData}
            {latestInvoiceData}
            {isUpdatingBillingStatus}
            {isFromCalendarList}
            {reloadBillingDetails}
        />

        {#if !isQuoteSectionsHidden}
            <div class="divider px-4">QUOTE DETAILS</div>

            {#if isLoadingDoctorPresets || isLoadingDoctorBillingRules}
                <PageLoading />
            {:else}
                <PatientEncounterItems
                    latestObjDetails={latestQuoteData}
                    itemsData={latestQuoteDataItems}
                    billingData={quotesData}
                    __BackupItemsData__={__BackupLatestQuoteItems__}
                    itemType="quote"
                    scenarioOneFulfilled={quote_scenario_one_fulfilled}
                    scenarioTwoFulfilled={quote_scenario_two_fulfilled}
                    {paymentsData}
                    {doctorPresets}
                    {doctorProviders}
                    {doctorBillingRules}
                    bind:patientEncounterDetails
                    bind:isRegenerating
                    bind:itemsNotSaved={quoteItemsNotSaved}
                    bind:showSelectBillingRule={
                        isEstimateItemsShowSelectBillingRule
                    }
                    bind:this={quoteItemsRef}
                    {reloadBillingDetails}
                    {regenerateQuoteOrInvoice}
                    {reloadBillingDetailsAndUpdateDefaultItems}
                />
            {/if}

            <PatientEncounterBillingLatestDetails
                bind:patientEncounterDetails
                {billingDetails}
                latestObjDetails={latestQuoteData}
                itemsData={latestQuoteDataItems}
                associateArr={quotesData}
                type="Quote"
                scenarioOneFulfilled={quote_scenario_one_fulfilled}
                scenarioTwoFulfilled={quote_latest_billing_data_scenario_two_fulfilled}
                {reloadBillingDetails}
                {reloadBillingDetailsAndUpdateDefaultItems}
                {reloadBillingItemsWhenTimeItemAdded}
                {regenerateQuoteOrInvoice}
            />

            <PatientEncounterQuotesTable
                bind:patientEncounterDetails
                {quotesData}
                {reloadPatientEncounter}
                {latestQuoteData}
                {latestQuoteDataItems}
                {latestInvoiceData}
                {latestInvoiceDataItems}
                {itemsNotSaved}
                isShowSelectBillingRule={isEstimateItemsShowSelectBillingRule}
                {totalPaymentsAmount}
                {reloadBillingDetails}
                {reloadBillingDetailsAndUpdateDefaultItems}
                on:quoteSendToPatient={onQuoteSendToPatient}
            />
        {/if}

        <div class="divider px-4">INVOICE DETAILS</div>

        {#if isLoadingDoctorPresets || isLoadingDoctorBillingRules}
            <PageLoading />
        {:else}
            <PatientEncounterItems
                latestObjDetails={latestInvoiceData}
                {latestQuoteData}
                {latestQuoteDataItems}
                itemsData={latestInvoiceDataItems}
                billingData={invoicesData}
                __BackupItemsData__={__BackupLatestInvoiceItems__}
                itemType="invoice"
                scenarioOneFulfilled={invoice_scenario_one_fulfilled}
                scenarioTwoFulfilled={invoice_scenario_two_fulfilled}
                {paymentsData}
                {doctorPresets}
                {doctorProviders}
                {doctorBillingRules}
                {isQuoteSectionsHidden}
                bind:patientEncounterDetails
                bind:isRegenerating
                bind:itemsNotSaved={invoiceItemsNotSaved}
                bind:showSelectBillingRule={isInvoiceItemsShowSelectBillingRule}
                bind:this={invoiceItemsRef}
                {reloadBillingDetails}
                {regenerateQuoteOrInvoice}
                {reloadBillingDetailsAndUpdateDefaultItems}
            />
        {/if}

        <PatientEncounterBillingLatestDetails
            bind:patientEncounterDetails
            {billingDetails}
            latestObjDetails={latestInvoiceData}
            latestQuoteObjDetails={latestQuoteData}
            associateArr={invoicesData}
            type="Invoice"
            scenarioOneFulfilled={invoice_scenario_one_fulfilled}
            scenarioTwoFulfilled={invoice_latest_billing_data_scenario_two_fulfilled}
            {reloadBillingDetails}
            {reloadBillingDetailsAndUpdateDefaultItems}
            {reloadBillingItemsWhenTimeItemAdded}
            {regenerateQuoteOrInvoice}
        />

        <PatientEncounterInvoicesTable
            bind:logsLoaded
            {patientEncounterDetails}
            {invoicesData}
            {paymentsData}
            {latestInvoiceData}
            {latestInvoiceDataItems}
            {itemsNotSaved}
            isShowSelectBillingRule={isInvoiceItemsShowSelectBillingRule}
            {totalPaymentsAmount}
            {paymentsTableRef}
            {reloadPatientEncounter}
            {reloadBillingDetails}
        />

        <div class="divider px-4">PAYMENTS</div>

        <PatientEncounterPaymentsTable
            bind:this={paymentsTableRef}
            {patientEncounterDetails}
            {paymentsData}
            {quotesData}
            {invoicesData}
            {latestQuoteData}
            {latestInvoiceData}
            {isUserDoctor}
            {isUserEnableSeePaymentReceive}
            {reloadBillingDetails}
            {regenerateQuoteOrInvoice}
        />
    </div>
</div>

<NotificationModal
    isModalOpen={showWarningExceedOutsideFundRulesModal}
    title="Warning"
    showCancelBtn={false}
    submitText="Acknowledge"
    confirmAction={_closeWarningExceedOutsideFundRules}
    closeModalFunction={_closeWarningExceedOutsideFundRules}
>
    <div class="flex flex-col gap-y-2 p-4">
        <p>
            The selected unit value of {getAmountInCurrency(
                latestInvoiceData?.calculationAmount,
            )}/unit has resulted in a calculated gap outside fund rules. This
            means the invoice will automatically switch to patient PAYS TOTAL
            FEE to comply with fund rules.
        </p>
        <p>
            If you wish for your patient to only pay the gap fee, please reduce
            your unit value.
        </p>
    </div>
</NotificationModal>
